import { ref, watch, type Ref } from 'vue';
import type { AiUsage } from '../types/ai';
import type { useWorkspaceStore } from '../stores/workspace';
import type { EntityDocument } from '../types/entities';

export function useEntityAuxActions(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  activeEntity: Readonly<Ref<EntityDocument | null>>;
  isVersionReadonly: Readonly<Ref<boolean>>;
  draft: { summary: string };
  scheduleSave: () => void;
  flushSave: () => Promise<void>;
  aiSummaryModel: Readonly<Ref<'luna' | 'terra' | 'sol'>>;
  aiSummaryTargetWords: Readonly<Ref<number>>;
  aiSummaryState: Ref<'idle' | 'loading' | 'error'>;
  aiSummaryError: Ref<string>;
  aiPromptState: Ref<'idle' | 'loading' | 'error'>;
  aiPromptError: Ref<string>;
  aiPromptText: Ref<string>;
  aiPromptOpen: Ref<boolean>;
}) {
  const { store, activeEntity, isVersionReadonly, draft, scheduleSave, flushSave, aiSummaryModel, aiSummaryTargetWords, aiSummaryState, aiSummaryError, aiPromptState, aiPromptError, aiPromptText, aiPromptOpen } = options;
  const aiSummaryUsage = ref<AiUsage | null>();
  watch(() => activeEntity.value?.id, () => { aiSummaryUsage.value = undefined; });
async function createEntityFieldHistory(input: { fieldKey: string; fieldValue: unknown; startSceneId?: string | null; startSegmentId?: string | null; endSceneId?: string | null; endSegmentId?: string | null; notes?: string | null }) {
  await store.createEntityFieldHistory(input).catch((reason) => { store.error = reason instanceof Error ? reason.message : 'Der Feldzustand konnte nicht gespeichert werden.'; });
}
async function updateEntityFieldHistory(id: string, input: { fieldKey: string; fieldValue: unknown; startSceneId?: string | null; startSegmentId?: string | null; endSceneId?: string | null; endSegmentId?: string | null; notes?: string | null }) {
  await store.updateEntityFieldHistory(id, input).catch((reason) => { store.error = reason instanceof Error ? reason.message : 'Der Feldzustand konnte nicht geändert werden.'; });
}
async function deleteEntityFieldHistory(id: string) {
  if (window.confirm('Diesen zeitlichen Feldzustand löschen?')) await store.deleteEntityFieldHistory(id).catch((reason) => { store.error = reason instanceof Error ? reason.message : 'Der Feldzustand konnte nicht gelöscht werden.'; });
}

async function generateAiSummary() {
  const requiresConfirmation = aiSummaryModel.value !== 'luna';
  if (requiresConfirmation && !window.confirm(`${aiSummaryModel.value === 'terra' ? 'Terra' : 'Sol'} wird für diese Kurzfassung verwendet. Die Nutzung dieses leistungsstärkeren Modells muss für jede Anfrage bestätigt werden. Fortfahren?`)) return;
  aiSummaryState.value = 'loading'; aiSummaryError.value = '';
  const entityId = activeEntity.value?.id;
  aiSummaryUsage.value = undefined;
  try { const result = await store.generateAiSummary(entityId, aiSummaryTargetWords.value, aiSummaryModel.value, requiresConfirmation); if (activeEntity.value?.id === entityId) aiSummaryUsage.value = result?.usage ?? null; aiSummaryState.value = 'idle'; }
  catch (error) { aiSummaryState.value = 'error'; aiSummaryError.value = error instanceof Error ? error.message : 'Die Zusammenfassung konnte nicht erstellt werden.'; }
}
async function applyAiSummaryAsDescription() {
  if (!activeEntity.value?.aiSummary || isVersionReadonly.value) return;
  draft.summary = activeEntity.value.aiSummary;
  scheduleSave();
  await flushSave();
}
async function showAiPrompt() {
  aiPromptState.value = 'loading'; aiPromptError.value = '';
  try {
    const result = await store.getAiSummaryPrompt(undefined, aiSummaryTargetWords.value);
    aiPromptText.value = result.prompt;
    aiPromptOpen.value = true;
    aiPromptState.value = 'idle';
  } catch (error) {
    aiPromptState.value = 'error';
    aiPromptError.value = error instanceof Error ? error.message : 'Der KI-Prompt konnte nicht geladen werden.';
  }
}
  return { createEntityFieldHistory, updateEntityFieldHistory, deleteEntityFieldHistory, generateAiSummary, applyAiSummaryAsDescription, showAiPrompt, aiSummaryUsage };
}
